import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Award } from 'lucide-react';
import { Button } from '../ui/button';
import { courseModules } from '../../data/courseData';
import { useProgress } from '../../context/ProgressContext';
import { CompletionScreen } from './CompletionScreen';
import { cn } from '../../lib/utils';

/**
 * Server-authoritative navigation: a module is "complete" when its quiz is passed on the server.
 * The quiz submission writes to the DB and refreshes progress — no explicit "Complete" button needed.
 */
export function ModuleNavigation({ currentModule }) {
  const navigate = useNavigate();
  const { isModuleComplete, isQuizPassed, getOverallProgress } = useProgress();
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);

  const currentIndex = courseModules.findIndex((m) => m.id === currentModule.id);
  const prevModule = currentIndex > 0 ? courseModules[currentIndex - 1] : null;
  const nextModule = currentIndex < courseModules.length - 1 ? courseModules[currentIndex + 1] : null;
  const isComplete = isModuleComplete(currentModule.id);
  const isLastModule = currentIndex === courseModules.length - 1;
  const quizPassed = isQuizPassed(currentModule.id);
  const allDone = getOverallProgress() === 100;

  return (
    <>
      <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8 mt-8">
        <div className="flex items-center justify-center gap-2 mb-6">
          {courseModules.map((m, index) => (
            <div
              key={m.id}
              className={cn(
                'w-3 h-3 rounded-full transition-all',
                index === currentIndex && 'w-8 bg-primary',
                index !== currentIndex && isModuleComplete(m.id) && 'bg-success',
                index !== currentIndex && !isModuleComplete(m.id) && 'bg-stone-200'
              )}
            />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="w-full sm:w-auto">
            {prevModule ? (
              <Button
                data-testid="nav-prev"
                variant="outline"
                onClick={() => navigate(`/module/${prevModule.slug}`)}
                className="w-full sm:w-auto rounded-full px-6 py-4 font-semibold nav-button"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                {prevModule.shortTitle}
              </Button>
            ) : (
              <Button
                data-testid="nav-home"
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full sm:w-auto rounded-full px-6 py-4 font-semibold nav-button"
              >
                <Home className="w-5 h-5 mr-2" />
                Home
              </Button>
            )}
          </div>

          <div className="w-full sm:w-auto">
            {isLastModule && allDone ? (
              <Button
                data-testid="nav-certificate"
                onClick={() => setShowCompletionScreen(true)}
                className="w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-8 py-6 text-lg font-bold nav-button"
              >
                <Award className="w-5 h-5 mr-2" />
                View Certificate
              </Button>
            ) : nextModule && isComplete ? (
              <Button
                data-testid="nav-next"
                onClick={() => navigate(`/module/${nextModule.slug}`)}
                className="w-full sm:w-auto bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold nav-button"
              >
                {nextModule.shortTitle}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : nextModule ? (
              <Button
                data-testid="nav-next-locked"
                disabled
                className="w-full sm:w-auto rounded-full px-8 py-6 text-lg font-bold bg-stone-200 text-stone-500 cursor-not-allowed"
              >
                Pass Quiz to Continue
              </Button>
            ) : (
              <Button
                data-testid="nav-certificate-locked"
                onClick={() => navigate('/certificate')}
                className="w-full sm:w-auto bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold nav-button"
              >
                <Award className="w-5 h-5 mr-2" />
                Certificate
              </Button>
            )}
          </div>
        </div>

        {!quizPassed && (
          <p className="text-center text-text-muted text-sm mt-4">
            Pass the quiz above to unlock the next module.
          </p>
        )}
      </div>

      {showCompletionScreen && (
        <CompletionScreen onClose={() => setShowCompletionScreen(false)} />
      )}
    </>
  );
}
